import React, { Fragment, useEffect, useRef } from 'react';
import { useLocalObservable, observer } from 'mobx-react';
import pick from 'lodash/pick';
import { Link } from 'react-router-dom';
import AzureFormDef from '../../forms/azure-sso';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { ThemedLink } from '../../components/themed/ThemedComponents';
import TextField from '../../components/inputs/textField';
import ErrorMessage from '../../components/errormessage';
import { Row, Col, Spinner } from 'reactstrap';
import { useStores } from '../../hooks/use-stores';
import { FormControlLabel, Switch } from '@mui/material';
import IntegrationDetailSidebar from '../../components/company/integration-detail-sidebar';
import {
    Heading1,
    Heading3,
    ThemedButton,
    TextInput,
    CopyToClipboardWrapper,
    TextDefault,
    TextTiny,
    BigModal,
    FlexDiv,
    Block,
} from '@awarego-ui/components';

function CompanyAzureSSO({ companyId }) {
    const { companyStore, brandingStore, commonStore } = useStores();
    const fileRef = useRef(null);
    const initialRef = useRef({});

    // Provisioning section state
    const [showGenerateKeyModal, setShowGenerateKeyModal] =
        React.useState(false);
    const [showGeneratedKey, setShowGeneratedKey] = React.useState(false);

    const store = useLocalObservable(() => ({
        _form: new AzureFormDef(
            {},
            {
                hooks: {
                    onSuccess: async () => {
                        const currentValues = store._form.values();
                        const previous = initialRef.current || {};
                        const changed = Object.entries(currentValues)
                            .filter(([key, value]) => value !== previous[key])
                            .reduce((acc, [key, value]) => {
                                acc[key] = value;
                                return acc;
                            }, {});

                        const isDisabled = initialRef.current?.enabled === '0';
                        if (isDisabled) {
                            changed.enabled = '1';
                        }

                        if (Object.keys(changed).length) {
                            await companyStore.saveIntegrationData(
                                companyId,
                                'azuresso',
                                changed
                            );
                            initialRef.current = {
                                ...currentValues,
                                enabled: '1',
                            };
                        }
                    },
                },
            }
        ),
        update(values) {
            store._form.update(values);
        },
    }));

    const doLoadData = async () => {
        await companyStore.loadIntegrationData(companyId, 'azuresso');

        let obj = companyStore.azuressoDataIndex[companyId];
        if (obj) {
            store.update(pick(obj, 'tenant', 'cert', 'appName', 'domain'));
        }
    };

    useEffect(() => {
        doLoadData();
    }, [companyId]);

    const loadAsPromise = (f) => {
        return new Promise(async (resolve, reject) => {
            let reader = new FileReader();

            reader.onload = function (e) {
                let text = reader.result;
                resolve(text);
            };
            reader.onabort = function () {
                reject();
            };

            reader.readAsText(f);
        });
    };

    const handleFileSelect = async (evt) => {
        evt.stopPropagation();
        evt.preventDefault();

        let files = evt.target.files || evt.dataTransfer.files;
        for (let i = 0; i < files.length; i++) {
            let f = files[i];

            if (
                f.type &&
                f.size > 0 &&
                !(f.size < 5000 && f.name.endsWith('cer'))
            ) {
                continue;
            }

            let text = await loadAsPromise(f);
            store._form.update({ cert: text });
            store._form.$('cert').validate();
            break;
        }
    };

    const handleDragOver = (evt) => {
        evt.stopPropagation();
        evt.preventDefault();
        evt.dataTransfer.dropEffect = 'copy'; // Explicitly show this is a copy.
    };

    const chooseFile = (e) => {
        fileRef.current.click();
    };

    const onToggleEnabled = async () => {
        await companyStore.saveIntegrationData(companyId, 'azuresso', {
            enabled: '0',
        });
        initialRef.current.enabled = '0';
    };

    const handleGenerateScimKey = () => {
        setShowGenerateKeyModal(true);
    };

    const confirmGenerateScimKey = async () => {
        setShowGenerateKeyModal(false);
        try {
            await companyStore.generateScimKey(companyId);
            setShowGeneratedKey(true);
        } catch (error) {
            // Error is handled in the store
        }
    };

    const closeGeneratedKeyModal = () => {
        setShowGeneratedKey(false);
        companyStore.clearScimKey();
    };

    const form = (
        <Fragment>
            <TextField field={store._form.$('tenant')} minWidth={'350px'} />
            <input
                className="hidden"
                type="file"
                multiple={false}
                accept=".cer"
                onChange={handleFileSelect}
                ref={fileRef}
            />
            <TextField
                component={'textarea'}
                rows={8}
                suffix={
                    <ThemedLink onClick={chooseFile}> &nbsp; browse</ThemedLink>
                }
                field={store._form.$('cert')}
            />
            <TextField field={store._form.$('appName')} />
            <TextField field={store._form.$('domain')} />
        </Fragment>
    );

    const {
        savingAzuressoData,
        loadingAzuressoDatas,
        azuressoError,
        generatingScimKey,
        scimKeyGenerated,
    } = companyStore;
    const { account_name, brandingLoaded, integrationSSOUrl } = brandingStore;
    let data = companyStore.azuressoDataIndex[companyId];

    useEffect(() => {
        if (data) {
            initialRef.current = pick(
                data,
                'tenant',
                'cert',
                'domain',
                'appName',
                'enabled'
            );
        }
    }, [data]);

    return loadingAzuressoDatas.includes(companyId) || !data ? (
        <ThemedSpinner />
    ) : (
        <Fragment>
            <Link to=".." relative="path">
                <div className={'navigation back-button'}>
                    Back to Integration overview
                </div>
            </Link>
            <Row>
                <Col
                    lg="8"
                    xs="12"
                    onDragOver={handleDragOver}
                    onDrop={handleFileSelect}
                >
                    <div className="integration-detail-block">
                        {azuressoError && (
                            <ErrorMessage
                                error={azuressoError}
                                marginBottom={true}
                            />
                        )}
                        <img
                            src={`/static/img/logo-azure-ad.svg`}
                            alt="Azure logo"
                        />
                        <Heading1 mb={8} mt={16}>
                            {(brandingLoaded && account_name) || 'AwareGO'} +
                            Azure Single Sign-On (SSO)
                        </Heading1>
                        <p
                            className={
                                data.enabled === '0' ? 'margin-bottom' : ''
                            }
                        >
                            Reduce sign-in friction and give your users
                            one-click access to the app.
                        </p>
                        {data.enabled === '0' ? (
                            <Fragment>
                                {form}
                                {savingAzuressoData.includes(companyId) ? (
                                    <Spinner />
                                ) : (
                                    <ThemedButton
                                        mode="primary"
                                        onClick={store._form.onSubmit}
                                        text="Connect Azure SSO"
                                    />
                                )}
                            </Fragment>
                        ) : (
                            ''
                        )}
                    </div>
                    {data.enabled === '1' ? (
                        <Fragment>
                            <div className="integration-detail-block">
                                <Heading3 my={8}>Manage connection</Heading3>
                                {form}
                                {savingAzuressoData.includes(companyId) ? (
                                    <Spinner />
                                ) : (
                                    <ThemedButton
                                        mode="primary"
                                        onClick={store._form.onSubmit}
                                        text="Save changes"
                                    />
                                )}
                                <Heading3 my={8}>Enable SSO</Heading3>
                                <div className="button-with-description">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={
                                                    Number(
                                                        data && data.enabled
                                                    ) === 1
                                                }
                                                onChange={onToggleEnabled}
                                                name="ssoEnabled"
                                                color="primary"
                                            />
                                        }
                                        label="Enable Single Sign-On to all users"
                                    />
                                </div>
                                <Heading3 my={8}>
                                    Provisioning{' '}
                                    <span
                                        style={{
                                            backgroundColor: '#007bff',
                                            color: 'white',
                                            fontSize: '10px',
                                            padding: '2px 6px',
                                            borderRadius: '3px',
                                            marginLeft: '8px',
                                            fontWeight: 'normal',
                                        }}
                                    >
                                        Beta
                                    </span>
                                </Heading3>
                                <div className="provisioning-section">
                                    <TextDefault mb={16}>
                                        SCIM (System for Cross-domain Identity
                                        Management) allows you to automatically
                                        provision and manage users in your
                                        organization.
                                    </TextDefault>
                                    <Block mb={16}>
                                        <TextDefault mb={8}>
                                            <strong>SCIM URL:</strong>
                                        </TextDefault>
                                        <FlexDiv alignCenter gap={8}>
                                            <TextInput
                                                value="https://api.awarego.com/scim"
                                                readOnly
                                                style={{
                                                    backgroundColor: '#f5f5f5',
                                                    flex: 1,
                                                }}
                                            />
                                            <CopyToClipboardWrapper
                                                copyText="https://api.awarego.com/scim"
                                                onCopied={commonStore.copied}
                                            />
                                        </FlexDiv>
                                    </Block>
                                    <Block mb={16}>
                                        <TextDefault mb={8}>
                                            <strong>SCIM Key:</strong>
                                        </TextDefault>
                                        <FlexDiv alignCenter gap={16}>
                                            {data.scimSecret ? (
                                                <TextInput
                                                    value={data.scimSecret}
                                                    readOnly
                                                    style={{
                                                        backgroundColor:
                                                            '#f5f5f5',
                                                    }}
                                                />
                                            ) : (
                                                <TextDefault color="#666">
                                                    No key generated
                                                </TextDefault>
                                            )}
                                            <ThemedButton
                                                onClick={handleGenerateScimKey}
                                                text={
                                                    data.scimSecret
                                                        ? 'Generate New Key'
                                                        : 'Generate Key'
                                                }
                                                mode="secondary"
                                                disabled={generatingScimKey.includes(
                                                    companyId
                                                )}
                                            />
                                            {generatingScimKey.includes(
                                                companyId
                                            ) && <Spinner />}
                                        </FlexDiv>
                                    </Block>
                                </div>
                            </div>
                        </Fragment>
                    ) : (
                        ''
                    )}
                </Col>
                <Col lg="4" xs="12">
                    <IntegrationDetailSidebar
                        href={integrationSSOUrl}
                        product="Azure SSO"
                    />
                </Col>
            </Row>

            {/* Generate Key Confirmation Modal */}
            <BigModal
                isOpen={showGenerateKeyModal}
                width={'500'}
                onRequestClose={() => setShowGenerateKeyModal(false)}
                heading="Generate SCIM Key"
                modalContent={
                    <TextDefault>
                        Are you sure you want to generate a new SCIM key? This
                        will replace the existing key and may affect any active
                        SCIM integrations.
                    </TextDefault>
                }
                modalFooterContent={
                    <Fragment>
                        <ThemedButton
                            mode="primary"
                            text="Generate Key"
                            onClick={confirmGenerateScimKey}
                            disabled={generatingScimKey.includes(companyId)}
                        />
                        <ThemedButton
                            mode="secondary"
                            onClick={() => setShowGenerateKeyModal(false)}
                            text="Cancel"
                        />
                        {generatingScimKey.includes(companyId) && <Spinner />}
                    </Fragment>
                }
            />

            {/* Generated Key Modal */}
            <BigModal
                isOpen={showGeneratedKey}
                width={'600'}
                onRequestClose={closeGeneratedKeyModal}
                heading="SCIM Key Generated"
                modalContent={
                    <Fragment>
                        <TextDefault mb={16}>
                            Your SCIM key has been generated successfully.
                            Please copy it now as it will not be shown again.
                        </TextDefault>
                        <Block mb={16}>
                            <TextDefault mb={8}>
                                <strong>SCIM Key:</strong>
                            </TextDefault>
                            <FlexDiv alignCenter gap={12}>
                                <TextInput
                                    value={scimKeyGenerated || ''}
                                    readOnly
                                    style={{
                                        backgroundColor: '#f5f5f5',
                                        fontFamily: 'monospace',
                                        minWidth: '400px',
                                    }}
                                />
                                <CopyToClipboardWrapper
                                    copyText={scimKeyGenerated || ''}
                                    onCopied={commonStore.copied}
                                />
                            </FlexDiv>
                        </Block>
                        <TextTiny color="#ff6b6b">
                            ⚠️ This key is shown only once. Make sure to copy
                            and store it securely.
                        </TextTiny>
                    </Fragment>
                }
                modalFooterContent={
                    <ThemedButton
                        mode="secondary"
                        onClick={closeGeneratedKeyModal}
                        text="Close"
                    />
                }
            />
        </Fragment>
    );
}

export default observer(CompanyAzureSSO);
